// Chrome-style tab strip: list of tabs + "+" button + inline rename on
// double-click + right-click context menu. Drag-to-reorder uses native
// HTML5 DnD (lightweight, sufficient for in-row swap; no dnd-kit dep
// needed since we don't drag tabs across drop targets).

import React, { useState, useRef, useEffect, useCallback } from 'react';

function TabContextMenu({ x, y, tab, onClose, onRename, onDuplicate, onCloseTab, onCloseOthers, totalTabs }) {
  const ref = useRef(null);
  useEffect(() => {
    const off = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    document.addEventListener('mousedown', off);
    return () => document.removeEventListener('mousedown', off);
  }, [onClose]);

  return (
    <div ref={ref} className="cockpit-tab-menu" style={{ left: x, top: y }}>
      {!tab.pinned && (
        <button type="button" onClick={() => { onRename(); onClose(); }}>Rename</button>
      )}
      <button type="button" onClick={() => { onDuplicate(); onClose(); }}>Duplicate</button>
      {!tab.pinned && (
        <button type="button" onClick={() => { onCloseTab(); onClose(); }}>Close</button>
      )}
      {totalTabs > 1 && (
        <button type="button" onClick={() => { onCloseOthers(); onClose(); }}>Close others</button>
      )}
    </div>
  );
}

function CockpitTabBar({ tabs, activeTabId, onActivate, onClose, onRename, onAdd, onDuplicate, onReorder }) {
  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const [menu, setMenu] = useState(null);
  const dragIndexRef = useRef(null);

  const startRename = useCallback((tab) => {
    // Pinned tabs are server-controlled — admin owns the name in the
    // cockpit_default_tabs Mongo doc. Renaming locally would silently
    // get clobbered on the next page reload's merge, so we just no-op.
    if (tab.pinned) return;
    setRenamingId(tab.id);
    setRenameValue(tab.name);
  }, []);

  const commitRename = useCallback(() => {
    if (renamingId) onRename(renamingId, renameValue);
    setRenamingId(null);
  }, [renamingId, renameValue, onRename]);

  const cancelRename = useCallback(() => {
    setRenamingId(null);
  }, []);

  const handleDragStart = (index) => (e) => {
    dragIndexRef.current = index;
    e.dataTransfer.effectAllowed = 'move';
    // Some browsers require setData to enable drag.
    try { e.dataTransfer.setData('text/plain', String(index)); } catch {}
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (index) => (e) => {
    e.preventDefault();
    const from = dragIndexRef.current;
    if (from == null || from === index) return;
    onReorder(from, index);
    dragIndexRef.current = null;
  };

  return (
    <div className="cockpit-tabs">
      {tabs.map((tab, index) => {
        const isActive = tab.id === activeTabId;
        const isRenaming = renamingId === tab.id;
        return (
          <div
            key={tab.id}
            className={`cockpit-tab ${isActive ? 'active' : ''} ${tab.pinned ? 'pinned' : ''}`}
            draggable={!isRenaming}
            onDragStart={handleDragStart(index)}
            onDragOver={handleDragOver}
            onDrop={handleDrop(index)}
            onClick={() => !isRenaming && onActivate(tab.id)}
            onDoubleClick={(e) => { e.stopPropagation(); startRename(tab); }}
            onContextMenu={(e) => {
              e.preventDefault();
              setMenu({ x: e.clientX, y: e.clientY, tab });
            }}
          >
            {isRenaming ? (
              <input
                type="text"
                className="cockpit-tab-rename"
                value={renameValue}
                autoFocus
                onChange={(e) => setRenameValue(e.target.value)}
                onBlur={commitRename}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') commitRename();
                  if (e.key === 'Escape') cancelRename();
                }}
                onClick={(e) => e.stopPropagation()}
              />
            ) : (
              <span className="cockpit-tab-name">{tab.name}</span>
            )}
            {!tab.pinned && !isRenaming && (
              <button
                type="button"
                className="cockpit-tab-close"
                onClick={(e) => { e.stopPropagation(); onClose(tab.id); }}
                title="Close tab"
                aria-label={`Close tab ${tab.name}`}
              >
                ×
              </button>
            )}
          </div>
        );
      })}
      <button type="button" className="cockpit-tab-add" onClick={onAdd} title="New tab">+</button>

      {menu && (
        <TabContextMenu
          x={menu.x}
          y={menu.y}
          tab={menu.tab}
          totalTabs={tabs.length}
          onClose={() => setMenu(null)}
          onRename={() => startRename(menu.tab)}
          onDuplicate={() => onDuplicate(menu.tab.id)}
          onCloseTab={() => onClose(menu.tab.id)}
          onCloseOthers={() => {
            // Close all non-pinned, non-current tabs.
            tabs.forEach((t) => {
              if (t.id !== menu.tab.id && !t.pinned) onClose(t.id);
            });
          }}
        />
      )}
    </div>
  );
}

export default CockpitTabBar;
